import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { validatorTypes } from "@data-driven-forms/react-form-renderer";
import { Button, Modal } from "react-bootstrap";
import { useCreateDepartment, useGetCompanies, useGetVerticals, useUpdateDepartment } from "../../../../../backend/masters";
import { DEFAULT_OPTIONS_PAYLOAD } from "../../../../common/Table";
import { ACTIONS, PATTERNS } from "../../../../common/Constants";
import { API_RESULT, ERROR_MESSAGES } from "../../../../../utils/constants";
import FormRenderer, { ComponentMapper, FormTemplate, componentTypes } from "../../../../common/FormRenderer";
import { getValue, preventDefault } from "../../../../../utils/common";
import PageLoader from "../../../../shared/PageLoader";
import { GetActionTitle } from "../../Master.constants";
import { ResponseModel } from "../../../../../models/responseModel";

function AddEditDepartment({ action, record : data, onClose, onSubmit }: any) {
    const [t] = useState(new Date().getTime());
    const [form, setForm] = useState<any>({});
    const [department, setDepartment] = useState<any>({ hideButtons: true });
    const [company, setCompany] = useState<string | null>(null);
    const { companies } = useGetCompanies({ ...DEFAULT_OPTIONS_PAYLOAD, filters: [{ columnName: 'isParent', value: 'true' }, { columnName: 'is_active_eq', value: 'true' }], t }, action === ACTIONS.ADD);
    const { verticals } = useGetVerticals({ ...DEFAULT_OPTIONS_PAYLOAD, filters: [{ columnName: 'company_id', value: company }], t }, action === ACTIONS.ADD && Boolean(company));
    const { createDepartment, creating } = useCreateDepartment((vertical: ResponseModel) => {
        if (vertical && vertical.error_messages.length > 0) {
            toast.error(vertical.error_messages.join(', '));
        } else {
            toast.success(`"${vertical.name}" created successfully.`);
            onSubmit();
        }
    }, errorCallback);
    const { updateDepartment, updating } = useUpdateDepartment((vertical: ResponseModel) => {
      if (vertical && vertical.error_messages.length > 0) {
        toast.error(vertical.error_messages.join(', '));
    } else {
        toast.success(`"${vertical.name}" updated successfully.`);
        onSubmit();
    }
    }, errorCallback);

    function errorCallback() {
        toast.error(ERROR_MESSAGES.DEFAULT);
    }

    const schema = {
        fields: [
            {
                component: componentTypes.SELECT,
                name: 'company',
                label: 'Company',
                validate: [
                    { type: validatorTypes.REQUIRED }
                ],
                options: companies,
                content: action !== ACTIONS.ADD ? getValue(department, 'company.label') : null
            },
            {
                component: componentTypes.SELECT,
                name: 'vertical',
                label: 'Vertical',
                validate: action === ACTIONS.ADD ? [
                    { type: validatorTypes.REQUIRED }
                ] : [],
                options: verticals,
                content: action !== ACTIONS.ADD ? getValue(department, 'vertical.label') : null
            },
            {
                component: componentTypes.TEXT_FIELD,
                name: 'name',
                label: 'Department Name',
                validate: [
                    { type: validatorTypes.REQUIRED },
                    {
                        type: validatorTypes.PATTERN, pattern: PATTERNS.GENERIC,
                        message: 'Should contain only  A-Z, a-z, 0-9, space, hyphen(-) and underscore(_)'
                    }
                ],
                content: null
            },
            {
                component: componentTypes.TEXT_FIELD,
                name: 'short_code',
                label: 'Department Code',
                validate: [
                    { type: validatorTypes.REQUIRED },
                    { type: validatorTypes.MAX_LENGTH, threshold: 10 },
                    { type: validatorTypes.PATTERN, pattern: /^[a-zA-Z0-9]{2,10}$/, message: 'Should be alphanumeric value of length 2' }
                ],
                styleClass: 'text-uppercase',
                content: null
            },
            {
                component: componentTypes.TEXTAREA,
                name: 'description',
                label: 'Description',
                content: null
            }
        ]
    };

    function debugForm(_form: any) {
        setForm(_form);
        setDepartment({ ...department, ..._form.values });
        if (action === ACTIONS.ADD) {
            const _company = (_form.values.company || {}).value || null;
            if (company !== _company) {
                setCompany(_company);
                setDepartment({ ..._form.values, vertical: null });
            }
        }
    }

    function submit(e: any) {
        preventDefault(e);
        if (form.valid) {
            const { short_code, name, description, vertical } = department;
            const request: any = {
                short_code: short_code.toUpperCase(),
                name: name.trim(),
                description: (description || '').trim(),
                vertical_id: vertical.value
            };

            if (action === ACTIONS.EDIT) {
                request['id'] = data.id;
                updateDepartment(request);
            } else {
                createDepartment(request);
            }
        }
    }

    useEffect(() => {
        if (data) {
            const { vertical } = data || {};
            const { id, name, company } = vertical || {};
            setDepartment({
                ...department,
                ...data,
                company: company && company.id ? { value: company.id, label: company.name } : null,
                vertical: id ? { value: id, label: name } : null
            });
        }
    }, [data]);

    return (
        <>
            <Modal show={true} backdrop="static" dialogClassName="drawer" animation={false}>
                <Modal.Header closeButton={true} onHide={onClose}>
                    <Modal.Title className="bg">{GetActionTitle('Department', action)}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <FormRenderer FormTemplate={FormTemplate}
                        initialValues={department}
                        componentMapper={ComponentMapper}
                        schema={schema}
                        debug={debugForm}
                    />
                </Modal.Body>
                <Modal.Footer className="d-flex justify-content-between">
                    {
                      <>
                        <Button variant="outline-secondary" className="btn btn-outline-secondary px-4" onClick={onClose}>{'Cancel'}</Button>
                        <Button variant="primary" onClick={submit} className="px-4" disabled={!form.valid}>{'Submit'}</Button>
                      </>

                    }
                </Modal.Footer>
            </Modal>
            {
                (creating || updating) &&
                <PageLoader>
                    {creating ? 'Creating Department...' : 'Updating Department...'}
                </PageLoader>
            }
        </>
    )
}

export default AddEditDepartment;